import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PartyPopper, Calendar, MapPin, ArrowRight, Gift, Coffee, Snowflake } from 'lucide-react';

const Hero: React.FC = () => {
  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Floating background elements */}
      <div className="absolute inset-0">
        <motion.div
          animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-10 w-20 h-20 bg-secondary-300 rounded-full opacity-30"
        />
        <motion.div
          animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-40 right-20 w-16 h-16 bg-primary-300 rounded-full opacity-40"
        />
        <motion.div
          animate={{ y: [0, -15, 0], x: [0, 10, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-40 left-20 w-24 h-24 bg-accent-300 rounded-full opacity-25"
        />
        <motion.div
          animate={{ y: [0, 25, 0], rotate: [0, 15, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-20 right-10 w-20 h-20 bg-secondary-400 rounded-full opacity-35"
        />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-screen text-center">
        {/* Logo and main title */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-8"
        >
          <div className="relative">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-8 bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 rounded-full opacity-30 blur-xl"
            />
            <h1 className="text-6xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary-300 via-primary-100 to-accent-200 mb-2 drop-shadow-lg">
              SUPERDIENA
            </h1>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-8"
          >
            {/* Free badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative inline-flex items-center bg-gradient-to-r from-green-400 via-emerald-500 to-green-500 text-white px-8 py-3 rounded-full text-lg md:text-xl font-bold uppercase tracking-wider shadow-2xl mb-6 transform hover:scale-105 transition-transform group"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-green-400 via-emerald-500 to-green-500 rounded-full blur-lg opacity-60 animate-pulse"></div>

              {/* Badge content */}
              <div className="relative flex items-center gap-3">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <PartyPopper className="w-6 h-6 text-yellow-200" />
                </motion.div>
                <span className="font-black drop-shadow-md">PILNĪGI BEZMAKSAS</span>
                <motion.div
                  animate={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                >
                  <PartyPopper className="w-6 h-6 text-yellow-200 transform scale-x-[-1]" />
                </motion.div>
              </div>
            </motion.div>

            {/* Main subtitle */}
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-xl md:text-3xl font-medium text-gray-200 leading-relaxed max-w-3xl mx-auto"
            >
              Bezmaksas pasākums
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-lg md:text-xl text-gray-300 mt-4 max-w-2xl mx-auto"
            >
              Pievienojies neaizmirstamam ziemas piedzīvojumam ar radošām darbnīcām, spēlēm un gardiem ēdieniem!
            </motion.p>

          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 text-lg text-gray-300"
          >
            <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full backdrop-blur-sm border border-white/10">
              <Calendar className="w-5 h-5 text-primary-300" />
              <span className="font-medium">26. decembris, 2025</span>
            </div>
            <div className="hidden md:block w-2 h-2 bg-gray-500 rounded-full"></div>
            <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full backdrop-blur-sm border border-white/10">
              <MapPin className="w-5 h-5 text-accent-300" />
              <span className="font-medium">"Dadzis", Viļāni</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-12"
          >
            <button
              onClick={() => {
                const registrationSection = document.getElementById('registration');
                if (registrationSection) {
                  registrationSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="group relative inline-flex items-center gap-3 px-8 py-4 bg-white text-primary-900 text-lg font-bold rounded-full shadow-2xl hover:shadow-[0_0_40px_-10px_rgba(255,255,255,0.5)] transition-all duration-300 hover:-translate-y-1"
            >
              <span>Pieteikties</span>
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
          </motion.div>
        </motion.div>


        {/* Scroll indicator 
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-1 h-3 bg-gray-400 rounded-full mt-2"
            />
          </motion.div>
           <p className="text-gray-500 text-sm mt-2">Ritini uz leju</p>
        </motion.div>
        */}
      </div>
    </section>
  );
};

export default Hero;
